/**
 * @element qds-text-input
 *
 * Single-line text entry - the reference implementation for the form-association
 * pattern used by all six QDS form controls.
 *
 * @attr {string} value - The current value; attribute is the initial value
 * @attr {string} name - Submitted form key
 * @attr {string} label - Rendered as a real <label for> in the shadow root
 * @attr {string} placeholder - Placeholder text (not a substitute for label)
 * @attr {string} hint - Helper text below the input
 * @attr {string} error-text - Overrides the built-in validation message
 * @attr {'text'|'search'|'email'|'tel'|'url'|'password'} type - Input type, default 'text'
 * @attr {'sm'|'md'|'lg'} size - Control height, default 'md'
 * @attr {boolean} disabled - Not focusable, not submitted
 * @attr {boolean} readonly - Focusable, submitted, secondary background
 * @attr {boolean} required - Field is required
 * @attr {boolean} invalid - Author-forced invalid state
 * @attr {number} maxlength - Maximum character count
 * @attr {number} minlength - Minimum character count
 * @attr {string} pattern - RegExp pattern for validation
 * @attr {string} autocomplete - Autocomplete hint for the browser
 * @attr {boolean} show-counter - Show character counter (requires maxlength)
 * @attr {boolean} clearable - Show a clear button when non-empty
 * @attr {'blur'|'input'|'submit'} validate-on - Validation timing, default 'blur'
 *
 * @property {string} value - Current value, synced with attribute both directions
 * @property {string} name - Form field name
 * @property {boolean} disabled - Whether the control is disabled
 * @property {boolean} readonly - Whether the control is read-only
 * @property {boolean} required - Whether the control is required
 * @property {QdsValidity} validity - Read-only validity state
 *
 * @csspart container - Outer container
 * @csspart label - Label element
 * @csspart required - Required marker span
 * @csspart wrapper - Input wrapper (border surface)
 * @csspart input - Inner input element
 * @csspart prefix - Prefix slot
 * @csspart suffix - Suffix slot
 * @csspart clear-button - Clear button
 * @csspart hint - Hint text
 * @csspart error - Error message
 * @csspart counter - Character counter
 *
 * @fires input - Every keystroke; bubbles, composed
 * @fires change - Value committed (blur or Enter); bubbles, composed
 * @fires qds-validity-change - Validity flips or message changes; detail: { component, valid, validity, message }
 * @fires qds-clear - Clear button pressed; detail: { component }
 *
 * @example
 * <qds-text-input label="Name" placeholder="Enter your name" required></qds-text-input>
 */

import { loadBaseCSS, adoptPrimeIconsStyles } from '../shared/button-utils.js';
import {
  attachInternalsSafe,
  ValidityTracker,
  dispatchValidityChange,
  generateControlId,
  isAttrTruthy,
  reflectBoolean,
  parseSize,
  parseValidateOn,
  getFormControlStyles,
} from '../shared/form-control.js';
import type { QdsValidity, QdsControlSize, QdsValidateOn } from '../../types/index';

const ALLOWED_TYPES = ['text', 'search', 'email', 'tel', 'url', 'password'] as const;
type InputType = typeof ALLOWED_TYPES[number];

function parseType(value: string | null): InputType {
  if (value && ALLOWED_TYPES.includes(value as InputType)) return value as InputType;
  return 'text';
}

export class QdsTextInput extends HTMLElement {
  static formAssociated = true;
  static qdsFieldConsumer = true;

  private _internals?: ElementInternals;
  private _tracker: ValidityTracker;
  private _id: string;
  private _value = '';
  private _initialValue = '';
  private _lastCommittedValue: string | null = null;
  private _hasBlurred = false;
  private _errorVisible = false;
  private _customValidityMessage = '';
  private _disabled = false;
  private _valueChangedFromInput = false;
  private _lastValid = true;
  private _lastMessage = '';

  // Bound handlers
  private _onInput = (e: Event): void => this._handleInput(e);
  private _onBlur = (): void => this._handleBlur();
  private _onKeyDown = (e: KeyboardEvent): void => this._handleKeyDown(e);
  private _onClearClick = (e: MouseEvent): void => this._handleClearClick(e);
  private _onNativeChange = (e: Event): void => e.stopPropagation();

  static get observedAttributes(): string[] {
    return [
      'value', 'name', 'label', 'placeholder', 'hint', 'error-text',
      'type', 'size', 'disabled', 'readonly', 'required', 'invalid',
      'maxlength', 'minlength', 'pattern', 'autocomplete',
      'show-counter', 'clearable', 'validate-on',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
    this._internals = attachInternalsSafe(this);
    // jsdom may return an ElementInternals object without a working `validity`
    // getter. Detect this and fall back to undefined so the ValidityTracker
    // uses its QdsValidity fallback instead of crashing.
    if (this._internals) {
      try {
        if (!this._internals.validity) {
          this._internals = undefined;
        }
      } catch {
        this._internals = undefined;
      }
    }
    this._tracker = new ValidityTracker(this._internals);
    this._id = generateControlId('text-input');
  }

  // ── Reflected properties ─────────────────────────────────

  get value(): string {
    return this._value;
  }

  set value(v: string) {
    const str = String(v ?? '');
    this._value = str;
    this.setAttribute('value', str);
    if (this.isConnected) {
      this._syncInputValue();
      this._commitFormValue();
      this._updateCounter();
      this._updateClearButton();
    }
  }

  get name(): string {
    return this.getAttribute('name') || '';
  }

  set name(v: string) {
    this.setAttribute('name', v);
  }

  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(v: boolean) {
    reflectBoolean(this, 'disabled', v);
  }

  get readonly(): boolean {
    return isAttrTruthy(this.getAttribute('readonly'));
  }

  set readonly(v: boolean) {
    reflectBoolean(this, 'readonly', v);
  }

  get required(): boolean {
    return isAttrTruthy(this.getAttribute('required'));
  }

  set required(v: boolean) {
    reflectBoolean(this, 'required', v);
  }

  get type(): string {
    return parseType(this.getAttribute('type'));
  }

  set type(v: string) {
    this.setAttribute('type', v);
  }

  get size(): QdsControlSize {
    return parseSize(this.getAttribute('size'));
  }

  set size(v: string) {
    this.setAttribute('size', v);
  }

  get label(): string {
    return this.getAttribute('label') || '';
  }

  set label(v: string) {
    this.setAttribute('label', v ?? '');
  }

  get placeholder(): string {
    return this.getAttribute('placeholder') || '';
  }

  set placeholder(v: string) {
    this.setAttribute('placeholder', v ?? '');
  }

  get hint(): string {
    return this.getAttribute('hint') || '';
  }

  set hint(v: string) {
    this.setAttribute('hint', v ?? '');
  }

  get errorText(): string {
    return this.getAttribute('error-text') || '';
  }

  set errorText(v: string) {
    this.setAttribute('error-text', v ?? '');
  }

  get invalid(): boolean {
    return isAttrTruthy(this.getAttribute('invalid'));
  }

  set invalid(v: boolean) {
    reflectBoolean(this, 'invalid', v);
  }

  get maxlength(): number | null {
    const v = this.getAttribute('maxlength');
    return v === null ? null : parseInt(v, 10);
  }

  set maxlength(v: number | null) {
    if (v === null) this.removeAttribute('maxlength');
    else this.setAttribute('maxlength', String(v));
  }

  get minlength(): number | null {
    const v = this.getAttribute('minlength');
    return v === null ? null : parseInt(v, 10);
  }

  set minlength(v: number | null) {
    if (v === null) this.removeAttribute('minlength');
    else this.setAttribute('minlength', String(v));
  }

  get pattern(): string {
    return this.getAttribute('pattern') || '';
  }

  set pattern(v: string) {
    if (!v) this.removeAttribute('pattern');
    else this.setAttribute('pattern', v);
  }

  get autocomplete(): string {
    return this.getAttribute('autocomplete') || '';
  }

  set autocomplete(v: string) {
    if (!v) this.removeAttribute('autocomplete');
    else this.setAttribute('autocomplete', v);
  }

  get showCounter(): boolean {
    return isAttrTruthy(this.getAttribute('show-counter'));
  }

  set showCounter(v: boolean) {
    reflectBoolean(this, 'show-counter', v);
  }

  get clearable(): boolean {
    return isAttrTruthy(this.getAttribute('clearable'));
  }

  set clearable(v: boolean) {
    reflectBoolean(this, 'clearable', v);
  }

  get validateOn(): QdsValidateOn {
    return parseValidateOn(this.getAttribute('validate-on'));
  }

  set validateOn(v: QdsValidateOn) {
    this.setAttribute('validate-on', v);
  }

  get validity(): QdsValidity {
    this._computeValidity();
    return this._tracker.validity;
  }

  get form(): HTMLFormElement | null {
    return this._internals?.form ?? null;
  }

  // ── Form-association API ─────────────────────────────────

  checkValidity(): boolean {
    this._computeValidity();
    return this._tracker.checkValidity();
  }

  reportValidity(): boolean {
    this._computeValidity();
    const valid = this._tracker.reportValidity();
    this._errorVisible = true;
    this._maybeDispatchValidityChange();
    this._syncError();
    this._syncAria();
    return valid;
  }

  setCustomValidity(message: string): void {
    this._customValidityMessage = message;
    if (this.isConnected) {
      this._computeValidity();
      this._maybeDispatchValidityChange();
      this._syncError();
      this._syncAria();
    }
  }

  formResetCallback(): void {
    this._value = this._initialValue;
    this.setAttribute('value', this._initialValue);
    this._hasBlurred = false;
    this._errorVisible = false;
    this._lastCommittedValue = this._initialValue;
    this._syncInputValue();
    this._computeValidity();
    this._lastValid = this._tracker.valid;
    this._lastMessage = this._tracker.message;
    this._syncError();
    this._syncAria();
    this._updateCounter();
    this._updateClearButton();
    this._commitFormValue();
  }

  formDisabledCallback(d: boolean): void {
    reflectBoolean(this, 'disabled', d);
  }

  // ── Lifecycle ────────────────────────────────────────────

  connectedCallback(): void {
    if (!this.isConnected) return;
    this._initialValue = this.getAttribute('value') || '';
    this._value = this._initialValue;
    this._lastCommittedValue = this._value;
    this._disabled = isAttrTruthy(this.getAttribute('disabled'));
    this.render();
    this._syncInputValue();
    this._syncDom();
    this._commitFormValue();
  }

  disconnectedCallback(): void {
    this._removeListeners();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'value':
        this._value = newValue || '';
        if (this.isConnected && !this._valueChangedFromInput) {
          this._syncInputValue();
          this._commitFormValue();
          this._updateCounter();
          this._updateClearButton();
        }
        break;
      case 'disabled':
        this._disabled = isAttrTruthy(newValue);
        if (this.isConnected) this._syncDisabled();
        break;
      default:
        if (this.isConnected) {
          this._syncDom();
          this._computeValidity();
          this._maybeDispatchValidityChange();
          this._syncError();
          this._syncAria();
        }
        break;
    }
  }

  // ── Rendering ────────────────────────────────────────────

  private render(): void {
    if (!this.shadowRoot) return;

    this._removeListeners();

    this.shadowRoot.innerHTML = `
      <style>
        ${getFormControlStyles()}

        :host {
          --qds-input-height: var(--qds-control-height-md, 40px);
          --qds-input-background: var(--theme-background-primary, #fff);
          --qds-input-border-color: var(--theme-border-primary, #E4E9F2);
          --qds-input-border-color-hover: var(--theme-border-secondary, #AFBBD1);
          --qds-input-border-color-focus: var(--theme-accent-primary, #445E89);
          --qds-input-radius: var(--theme-radius-sm, 4px);
          --qds-input-padding-x: var(--theme-space-3, 12px);
        }

        .qds-container {
          display: inline-flex;
          flex-direction: column;
          width: 100%;
        }

        .qds-wrapper {
          display: flex;
          align-items: center;
          gap: var(--theme-space-1, 4px);
          height: var(--qds-input-height, var(--qds-control-height, 40px));
          border: 1px solid var(--qds-input-border-color, var(--theme-border-primary, #E4E9F2));
          border-radius: var(--qds-input-radius, var(--theme-radius-sm, 4px));
          background: var(--qds-input-background, var(--theme-background-primary, #fff));
          padding: 0 var(--qds-input-padding-x, var(--theme-space-3, 12px));
          box-sizing: border-box;
          transition: border-color var(--transition-speed-fast, 0.15s),
                      background-color var(--transition-speed-fast, 0.15s),
                      box-shadow var(--transition-speed-fast, 0.15s);
          font-size: 14px;
          font-family: inherit;
        }
        :host([size="lg"]) .qds-wrapper {
          padding: 0 var(--theme-space-4, 16px);
        }

        .qds-wrapper:hover {
          border-color: var(--qds-input-border-color-hover, var(--theme-border-secondary, #AFBBD1));
        }
        .qds-wrapper:focus-within {
          border-color: var(--qds-input-border-color-focus, var(--theme-accent-primary, #445E89));
          box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
          outline: none;
        }

        :host([readonly]) .qds-wrapper {
          background: var(--theme-background-secondary, #F8F9FA);
          cursor: default;
        }
        :host([disabled]) {
          opacity: 0.55;
          cursor: not-allowed;
          pointer-events: none;
        }
        :host([disabled]) .qds-wrapper {
          color: var(--theme-text-tertiary, #6A6A6A);
        }

        :host([invalid]) .qds-wrapper,
        :host([data-invalid]) .qds-wrapper {
          border-color: var(--theme-error, #8B1F00);
          background: var(--theme-background-error-subtle, rgba(139, 31, 0, 0.10));
        }
        :host([invalid]) .qds-wrapper:focus-within,
        :host([data-invalid]) .qds-wrapper:focus-within {
          border-color: var(--theme-error, #8B1F00);
          box-shadow: 0 0 0 2px var(--theme-error, rgba(139, 31, 0, 0.25));
        }

        .qds-input {
          flex: 1 1 auto;
          border: none;
          outline: none;
          background: transparent;
          font-family: inherit;
          font-size: 14px;
          color: var(--theme-text-primary, #333);
          padding: 0;
          height: 100%;
          min-width: 0;
          -webkit-appearance: none;
          appearance: none;
        }
        .qds-input::placeholder {
          color: var(--theme-text-tertiary, #6A6A6A);
        }
        :host([disabled]) .qds-input {
          color: var(--theme-text-tertiary, #6A6A6A);
        }

        .qds-clear-button {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          min-width: 32px;
          min-height: 32px;
          border: none;
          border-radius: var(--theme-radius-sm, 4px);
          background: transparent;
          cursor: pointer;
          color: var(--theme-text-tertiary, #6A6A6A);
          padding: 0;
          font-size: var(--icon-size, 20px);
          transition: background-color var(--transition-speed-fast, 0.15s),
                      color var(--transition-speed-fast, 0.15s);
        }
        .qds-clear-button:hover {
          background: var(--button-hover-bg, rgba(0, 0, 0, 0.05));
          color: var(--theme-text-secondary, #555);
        }
        .qds-clear-button:focus-visible {
          outline: none;
          box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
        }
        @media (pointer: coarse) {
          .qds-clear-button {
            width: 44px;
            height: 44px;
            min-width: 44px;
            min-height: 44px;
          }
        }

        .qds-meta {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: var(--theme-space-2, 8px);
        }

        .qds-counter {
          font-size: 13px;
          color: var(--theme-text-tertiary, #6A6A6A);
          margin-top: var(--theme-space-1, 4px);
          white-space: nowrap;
        }

        slot[name="prefix"],
        slot[name="suffix"] {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          color: var(--icon-color-secondary, var(--theme-text-tertiary, #6A6A6A));
          font-size: var(--icon-size, 20px);
        }
      </style>

      <div part="container" class="qds-container">
        <label part="label" for="${this._id}" class="qds-label" style="display:none"></label>
        <div part="wrapper" class="qds-wrapper">
          <slot name="prefix" part="prefix"></slot>
          <input part="input" id="${this._id}" class="qds-input" type="text" />
          <button part="clear-button" class="qds-clear-button" style="display:none" tabindex="-1" aria-label="Clear input">
            <i class="pi pi-times"></i>
          </button>
          <slot name="suffix" part="suffix"></slot>
        </div>
        <div class="qds-meta">
          <div part="hint" id="${this._id}-hint" class="qds-hint" style="display:none"></div>
          <div part="counter" class="qds-counter" style="display:none"></div>
        </div>
        <div part="error" id="${this._id}-error" class="qds-error" role="status" aria-live="polite" style="display:none"></div>
      </div>
    `;

    void adoptPrimeIconsStyles(this.shadowRoot);
    this._addListeners();
  }

  // ── DOM sync (lightweight, no re-render) ─────────────────

  private _syncDom(): void {
    if (!this.shadowRoot) return;
    const input = this.shadowRoot.querySelector<HTMLInputElement>('.qds-input');
    if (!input) return;

    input.type = parseType(this.getAttribute('type'));
    input.name = this.getAttribute('name') || '';
    input.placeholder = this.getAttribute('placeholder') || '';

    const maxlength = this.getAttribute('maxlength');
    if (maxlength !== null) input.setAttribute('maxlength', maxlength);
    else input.removeAttribute('maxlength');

    const minlength = this.getAttribute('minlength');
    if (minlength !== null) input.setAttribute('minlength', minlength);
    else input.removeAttribute('minlength');

    const pattern = this.getAttribute('pattern');
    if (pattern !== null) input.setAttribute('pattern', pattern);
    else input.removeAttribute('pattern');

    const autocomplete = this.getAttribute('autocomplete');
    if (autocomplete !== null) input.setAttribute('autocomplete', autocomplete);
    else input.removeAttribute('autocomplete');

    const required = isAttrTruthy(this.getAttribute('required'));
    if (required) input.setAttribute('required', '');
    else input.removeAttribute('required');

    const readonly = isAttrTruthy(this.getAttribute('readonly'));
    if (readonly) input.setAttribute('readonly', '');
    else input.removeAttribute('readonly');

    this._syncLabel();

    const hint = this.getAttribute('hint') || '';
    const hintEl = this.shadowRoot.querySelector<HTMLElement>('[part="hint"]');
    if (hintEl) {
      hintEl.textContent = hint;
      hintEl.style.display = hint ? 'block' : 'none';
    }

    this._syncError();
    this._syncAria();
    this._updateCounter();
    this._updateClearButton();
    this._syncDisabled();
  }

  private _syncLabel(): void {
    if (!this.shadowRoot) return;
    const labelEl = this.shadowRoot.querySelector<HTMLElement>('[part="label"]');
    if (!labelEl) return;

    const label = this.getAttribute('label') || '';
    const required = isAttrTruthy(this.getAttribute('required'));

    if (label) {
      labelEl.style.display = 'block';
      labelEl.textContent = label;
      if (required) {
        const marker = document.createElement('span');
        marker.setAttribute('part', 'required');
        marker.className = 'qds-required-marker';
        marker.setAttribute('aria-hidden', 'true');
        marker.textContent = '*';
        labelEl.appendChild(marker);
      }
    } else {
      labelEl.style.display = 'none';
      labelEl.textContent = '';
    }

    const clearBtn = this.shadowRoot.querySelector<HTMLButtonElement>('[part="clear-button"]');
    if (clearBtn) {
      clearBtn.setAttribute('aria-label', `Clear ${label || 'input'}`);
    }
  }

  private _syncError(): void {
    if (!this.shadowRoot) return;
    const errorEl = this.shadowRoot.querySelector<HTMLElement>('[part="error"]');
    if (!errorEl) return;

    const authorInvalid = isAttrTruthy(this.getAttribute('invalid'));
    const validationInvalid = !this._tracker.valid;
    const shouldShow = this._errorVisible && (authorInvalid || validationInvalid);

    let message = '';
    if (shouldShow) {
      message = this._tracker.message;
      if (!message) {
        const errorTextAttr = this.getAttribute('error-text');
        message = errorTextAttr || 'Invalid value.';
      }
    }

    errorEl.textContent = message;
    errorEl.style.display = shouldShow ? 'block' : 'none';

    if (shouldShow) {
      this.setAttribute('data-invalid', '');
    } else {
      this.removeAttribute('data-invalid');
    }
  }

  private _syncAria(): void {
    if (!this.shadowRoot) return;
    const input = this.shadowRoot.querySelector<HTMLInputElement>('.qds-input');
    if (!input) return;

    const required = isAttrTruthy(this.getAttribute('required'));
    const authorInvalid = isAttrTruthy(this.getAttribute('invalid'));
    const validationInvalid = !this._tracker.valid;
    const ariaInvalid = authorInvalid || (this._errorVisible && validationInvalid);

    input.setAttribute('aria-required', String(required));
    input.setAttribute('aria-invalid', String(ariaInvalid));

    const hintId = `${this._id}-hint`;
    const errorId = `${this._id}-error`;
    const describedBy: string[] = [];
    const hint = this.getAttribute('hint');
    if (hint) describedBy.push(hintId);
    if (ariaInvalid) describedBy.push(errorId);
    if (describedBy.length > 0) {
      input.setAttribute('aria-describedby', describedBy.join(' '));
    } else {
      input.removeAttribute('aria-describedby');
    }
  }

  private _syncDisabled(): void {
    if (!this.shadowRoot) return;
    const input = this.shadowRoot.querySelector<HTMLInputElement>('.qds-input');
    if (input) {
      if (this._disabled) input.setAttribute('disabled', '');
      else input.removeAttribute('disabled');
    }
    const clearBtn = this.shadowRoot.querySelector<HTMLButtonElement>('[part="clear-button"]');
    if (clearBtn) {
      if (this._disabled) clearBtn.setAttribute('disabled', '');
      else clearBtn.removeAttribute('disabled');
    }
  }

  private _syncInputValue(): void {
    if (!this.shadowRoot) return;
    const input = this.shadowRoot.querySelector<HTMLInputElement>('.qds-input');
    if (input) {
      input.value = this._value;
    }
  }

  private _updateCounter(): void {
    if (!this.shadowRoot) return;
    const counterEl = this.shadowRoot.querySelector<HTMLElement>('[part="counter"]');
    if (!counterEl) return;

    const showCounter = isAttrTruthy(this.getAttribute('show-counter'));
    const maxlength = this.getAttribute('maxlength');

    if (showCounter && maxlength !== null) {
      const max = parseInt(maxlength, 10);
      counterEl.textContent = `${this._value.length}/${max}`;
      counterEl.style.display = 'block';
    } else {
      counterEl.textContent = '';
      counterEl.style.display = 'none';
    }
  }

  private _updateClearButton(): void {
    if (!this.shadowRoot) return;
    const clearBtn = this.shadowRoot.querySelector<HTMLButtonElement>('[part="clear-button"]');
    if (!clearBtn) return;

    const clearable = isAttrTruthy(this.getAttribute('clearable'));
    const shouldShow = clearable && this._value.length > 0;

    clearBtn.style.display = shouldShow ? 'flex' : 'none';
    clearBtn.tabIndex = shouldShow ? 0 : -1;
  }

  // ── Validation ───────────────────────────────────────────

  private _computeValidity(): QdsValidity {
    const value = this._value;
    const required = isAttrTruthy(this.getAttribute('required'));
    const pattern = this.getAttribute('pattern');
    const type = parseType(this.getAttribute('type'));

    const flags: Partial<QdsValidity> = {
      valueMissing: required && value === '',
      typeMismatch: false,
      patternMismatch: false,
      rangeUnderflow: false,
      rangeOverflow: false,
      stepMismatch: false,
      customError: !!this._customValidityMessage,
    };

    if (pattern && value) {
      try {
        const re = new RegExp(pattern);
        flags.patternMismatch = !re.test(value);
      } catch {
        /* invalid regex - skip */
      }
    }

    if (type === 'email' && value) {
      flags.typeMismatch = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    } else if (type === 'url' && value) {
      try {
        new URL(value);
      } catch {
        flags.typeMismatch = true;
      }
    }

    const valid = !flags.valueMissing
      && !flags.typeMismatch
      && !flags.patternMismatch
      && !flags.customError;

    let message = '';
    if (flags.customError) {
      message = this._customValidityMessage;
    } else if (flags.valueMissing) {
      message = 'Please fill out this field.';
    } else if (flags.typeMismatch) {
      message = type === 'email'
        ? 'Please enter a valid email address.'
        : 'Please enter a valid URL.';
    } else if (flags.patternMismatch) {
      message = 'Please match the requested format.';
    }

    const errorTextAttr = this.getAttribute('error-text');
    if (errorTextAttr && !valid) {
      message = errorTextAttr;
    }

    this._tracker.setValidity({ ...flags, valid }, message);
    return this._tracker.validity;
  }

  private _validate(showError: boolean): void {
    this._computeValidity();
    if (showError) {
      this._errorVisible = true;
    }
    this._maybeDispatchValidityChange();
    this._syncError();
    this._syncAria();
  }

  private _maybeDispatchValidityChange(): void {
    const currentValid = this._tracker.valid;
    const currentMessage = this._tracker.message;
    if (currentValid !== this._lastValid || currentMessage !== this._lastMessage) {
      this._lastValid = currentValid;
      this._lastMessage = currentMessage;
      dispatchValidityChange(this, this._tracker);
    }
  }

  // ── Event handlers ───────────────────────────────────────

  private _handleInput(e: Event): void {
    if (this._disabled) return;
    const input = e.target as HTMLInputElement;
    this._value = input.value;
    this._valueChangedFromInput = true;
    this.setAttribute('value', input.value);
    this._valueChangedFromInput = false;

    this._commitFormValue();
    this._updateCounter();
    this._updateClearButton();

    e.stopPropagation();
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));

    const validateOn = parseValidateOn(this.getAttribute('validate-on'));
    if (validateOn === 'input') {
      this._validate(true);
    } else if (validateOn === 'blur' && this._errorVisible) {
      this._validate(true);
    }
  }

  private _handleBlur(): void {
    if (this._disabled) return;

    const validateOn = parseValidateOn(this.getAttribute('validate-on'));
    if (validateOn === 'blur') {
      this._hasBlurred = true;
      this._validate(true);
    }

    this._commitAndFireChange();
  }

  private _handleKeyDown(e: KeyboardEvent): void {
    if (this._disabled) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      this._commitAndFireChange();
    } else if (e.key === 'Escape') {
      if (isAttrTruthy(this.getAttribute('clearable')) && this._value) {
        e.preventDefault();
        this._clearValue();
      }
    }
  }

  private _handleClearClick(e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    if (this._disabled) return;
    this._clearValue();
  }

  private _clearValue(): void {
    this._value = '';
    this._valueChangedFromInput = true;
    this.setAttribute('value', '');
    this._valueChangedFromInput = false;

    this._syncInputValue();
    this._commitFormValue();
    this._updateCounter();
    this._updateClearButton();

    this.dispatchEvent(new CustomEvent('qds-clear', {
      bubbles: true,
      composed: true,
      detail: { component: this },
    }));

    this._commitAndFireChange();

    const validateOn = parseValidateOn(this.getAttribute('validate-on'));
    if (validateOn === 'input' || (validateOn === 'blur' && this._errorVisible)) {
      this._validate(true);
    }

    const input = this.shadowRoot?.querySelector<HTMLInputElement>('.qds-input');
    input?.focus();
  }

  private _commitAndFireChange(): void {
    if (this._value !== this._lastCommittedValue) {
      this._lastCommittedValue = this._value;
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    }
  }

  private _commitFormValue(): void {
    this._internals?.setFormValue(this._value);
  }

  // ── Listener management ──────────────────────────────────

  private _addListeners(): void {
    const input = this.shadowRoot?.querySelector<HTMLInputElement>('.qds-input');
    if (input) {
      input.addEventListener('input', this._onInput);
      input.addEventListener('blur', this._onBlur);
      input.addEventListener('keydown', this._onKeyDown);
      input.addEventListener('change', this._onNativeChange);
    }
    const clearBtn = this.shadowRoot?.querySelector<HTMLButtonElement>('[part="clear-button"]');
    if (clearBtn) {
      clearBtn.addEventListener('click', this._onClearClick);
    }
  }

  private _removeListeners(): void {
    const input = this.shadowRoot?.querySelector<HTMLInputElement>('.qds-input');
    if (input) {
      input.removeEventListener('input', this._onInput);
      input.removeEventListener('blur', this._onBlur);
      input.removeEventListener('keydown', this._onKeyDown);
      input.removeEventListener('change', this._onNativeChange);
    }
    const clearBtn = this.shadowRoot?.querySelector<HTMLButtonElement>('[part="clear-button"]');
    if (clearBtn) {
      clearBtn.removeEventListener('click', this._onClearClick);
    }
  }
}

if (!customElements.get('qds-text-input')) {
  customElements.define('qds-text-input', QdsTextInput);
}
